import React, { FC, useMemo, useState } from 'react';
import { Button, Col, Layout, Popover, Row, Table } from 'antd';
import { withRouter } from 'react-router';
import { connect } from 'react-redux';
import { nullSafeGet, nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import SubnavBar from '../../subnav_bar/SubnavBar';
import { EditOutlined, InfoCircleOutlined } from '@ant-design/icons';
import TroubleshootingAssicationsEditForm from './TroubleshootingAssicationsEditForm';
import { PageLoadingPlaceholder } from '../../page_loading_placeholder/PageLoadingPlaceholder';
import TSProblemTypesDisplay from './TSProblemTypesDisplay';
import TSLocationsDisplay from './TSLocationsDisplay';
import TSPrioritiesDisplay from './TSPrioritiesDisplay';
import OWTabTitleWithSubTitle from './OWTabTitleWithSubTitle';
import { isAssociationsNotSpecified } from '../../../utils/TroubleshootingUtils';
import { withUserType } from '../../withUserType';
import AccessPermissionChecker from '../../common/AccessPermissionChecker';
import { PERMISSION_NAMES } from '../../../utils/AuthUtils';

const { Content } = Layout;

const SelectedWithCountDisplay = ({ entities, children }) => {
	return (
		<div className="flex flex-col">
			{entities.length > 0 ? (
				<div className="mb-2 text-sm text-black/85">{`Selected: (${entities.length})`}</div>
			) : null}
			{children}
		</div>
	);
};

const TroubleshootingDetailAssociationsPage: FC<any> = ({
	troubleshootingGuide,
	fetching,
}): React.ReactElement => {
	const [editMode, setEditMode] = useState(false);

	const problemTypes = useMemo(
		() => nullSafeGetOrElse('problemTypes', troubleshootingGuide, []),
		[troubleshootingGuide]
	);
	const locations = useMemo(
		() => nullSafeGetOrElse('locations', troubleshootingGuide, []),
		[troubleshootingGuide]
	);
	const workOrderPriorities = useMemo(
		() => nullSafeGetOrElse('workOrderPriorities', troubleshootingGuide, []),
		[troubleshootingGuide]
	);

	const dataSource = useMemo(
		() => [
			{
				fieldName: 'Problem Types',
				values: (
					<SelectedWithCountDisplay entities={problemTypes}>
						<TSProblemTypesDisplay
							problemTypes={problemTypes}
							problemTypeIds={nullSafeGet('problemTypeIds', troubleshootingGuide)}
							notSpecified={isAssociationsNotSpecified(troubleshootingGuide)}
						/>
					</SelectedWithCountDisplay>
				),
			},
			{
				fieldName: 'Locations',
				values: (
					<SelectedWithCountDisplay entities={locations}>
						<TSLocationsDisplay
							locations={locations}
							locationIds={nullSafeGet('locationIds', troubleshootingGuide)}
							notSpecified={isAssociationsNotSpecified(troubleshootingGuide)}
						/>
					</SelectedWithCountDisplay>
				),
			},
			{
				fieldName: 'Priorities',
				values: (
					<SelectedWithCountDisplay entities={workOrderPriorities}>
						<TSPrioritiesDisplay
							priorities={workOrderPriorities}
							workOrderPriorityIds={nullSafeGet('workOrderPriorityIds', troubleshootingGuide)}
							notSpecified={isAssociationsNotSpecified(troubleshootingGuide)}
						/>
					</SelectedWithCountDisplay>
				),
			},
		],
		[locations, problemTypes, troubleshootingGuide, workOrderPriorities]
	);

	const onEditCancel = () => setEditMode(false);
	const onEdit = () => setEditMode(true);

	return fetching ? (
		<PageLoadingPlaceholder />
	) : (
		<Content
			style={{
				padding: '0 0.5em',
				backgroundColor: 'white',
			}}
		>
			<Row style={{ margin: '16px 4px' }} gutter={16}>
				<Col span={24}>
					<SubnavBar
						left={
							<OWTabTitleWithSubTitle
								title={'Associations'}
								subTitle={
									<div className="flex cursor-pointer flex-row items-center gap-2">
										<div>
											View, add, or change the locations, problem types, and priorities linked to
											this guide
										</div>
										<Popover
											trigger="hover"
											content="When creating a work order, the steps defined will be included in the creation flow if they match the associated location(s) & priority level(s), & problem type(s) specified here."
										>
											<InfoCircleOutlined />
										</Popover>
									</div>
								}
							/>
						}
						right={
							editMode ? null : (
								<AccessPermissionChecker
									name={PERMISSION_NAMES.MODIFY_TROUBLESHSHOOTING_ASSOCIATIONS}
								>
									<Button onClick={onEdit} icon={<EditOutlined />}>
										Edit
									</Button>
								</AccessPermissionChecker>
							)
						}
					/>
				</Col>
				<Col span={24} className="mt-4">
					{editMode ? (
						<TroubleshootingAssicationsEditForm
							troubleshootingGuide={troubleshootingGuide}
							onCancel={onEditCancel}
							onSuccess={onEditCancel}
						/>
					) : (
						<Table
							columns={[
								{
									dataIndex: 'fieldName',
									width: '30%',
								},
								{
									dataIndex: 'values',
								},
							]}
							showHeader={false}
							rowKey={(el) => el.fieldName}
							dataSource={dataSource}
							pagination={false}
						/>
					)}
				</Col>
			</Row>
		</Content>
	);
};

const mapStateToProps = (state) => ({
	fetching: state.troubleshooting_guides.fetching,
	troubleshootingGuide: state.troubleshooting_guides.detail,
	currentUser: state.session.currentUser,
});

export default withUserType(
	withRouter<any, any>(connect(mapStateToProps)(TroubleshootingDetailAssociationsPage))
);
